import React, { createContext } from 'react';
import classNames from 'classnames';
import {
    MobileSearchPanel,
    EmailConfirmationBar,
    LowBalanceNotificationBar,
    CreditsNotificationModal,
    BottomBarMobile,
} from '../../components';
import {
    handleLogOutSuccess,
    redirectToURLWithModalState,
    redirectToURLWithoutModalState,
} from './Topbar.helpers';
import { compose } from 'redux';
import { injectIntl } from '../../util/reactIntl';
import { withViewport } from '../../util/contextHelpers';
import { parse } from '../../util/urlHelpers';
import { supportedCountriesGoogleMapConfig } from '../../util/googleMaps';
import { ensureCurrentUser } from '../../util/data';
import GenericError from './GenericError';
import DeleteAccountNotificationModal from '../../containers/DeleteAccountPage/DeleteAccountNotificationModal';
import TopbarDesktop from './components/TopbarDesktop/TopbarDesktop';
import TopbarMobile from './components/TopbarMobile/TopbarMobile';
import css from './Topbar.css';
import { useCurrentEntityLocation } from '../../hooks/useCurrentEntityLocation';
import { useTopbarIndicators } from './hooks/useTopbarIndicators';

export const MAX_MOBILE_SCREEN_WIDTH = 1024;

export const TopbarContext = createContext(undefined);

const TopbarComponent = props => {
    const {
        className,
        rootClassName,
        desktopClassName,
        mobileRootClassName,
        mobileClassName,
        isAuthenticated,
        authInProgress,
        authInfoLoaded,
        currentUser,
        currentUserHasPublishedListings,
        currentUserHasOrders,
        currentPage,
        viewport,
        intl,
        onResendVerificationEmail,
        sendVerificationEmailInProgress,
        sendVerificationEmailError,
        wishlistListingStatusChanged,
        address: searchAddress,
        onLogout,
        showEmailNotificationBar = true,
        history,
        location,
        ...rest
    } = props;

    const { profileNotComplete, notificationCount, hasDraftListing } = useTopbarIndicators();

    const showNewListingsStatisticsForLoggedOutUser =
        !isAuthenticated && !authInProgress && !currentUser?.id?.uuid && authInfoLoaded;

    const currentUserGeoData = useCurrentEntityLocation({
        allowed: showNewListingsStatisticsForLoggedOutUser,
    });
    const { mobilesearch, address, bounds } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
    });

    const handleMobileSearchOpen = () => redirectToURLWithModalState(props, 'mobilesearch');
    const handleMobileSearchClose = () => redirectToURLWithoutModalState(props, 'mobilesearch');

    const handleLogOut = () => onLogout().then(() => handleLogOutSuccess(history));
    const getLocationData = () => {
        // Only render current search if full place object is available in the URL params
        const locationFieldsPresent = address && bounds;

        const noSearchLocationData = locationFieldsPresent
            ? {
                  search: address,
                  selectedPlace: { address, bounds },
              }
            : null;

        const searchData = noSearchLocationData;
        const countryRegExp = new RegExp(
            Object.values(supportedCountriesGoogleMapConfig)
                .map(({ countryString }) => countryString)
                .join('|'),
            'gi'
        );

        searchData &&
            searchData.search &&
            (searchData.search = searchData.search.replace(countryRegExp, ''));

        return searchData;
    };

    const isMobileLayout = viewport.width <= MAX_MOBILE_SCREEN_WIDTH;
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

    const {
        attributes: {
            profile: {
                publicData: { userType },
            },
        },
    } = ensureCurrentUser(currentUser);

    const initialSearchFormValues = { location: getLocationData() };
    const classes = classNames(rootClassName || css.root, className);

    return (
        <TopbarContext.Provider
            value={{
                showNewListingsStatisticsForLoggedOutUser,
                currentUserGeoData,
            }}
        >
            <div className={classes} data-role="top-bar">
                {showEmailNotificationBar && (
                    <>
                        <EmailConfirmationBar />
                        <LowBalanceNotificationBar />
                    </>
                )}
                {/** data-role=top-bar is used for wisepop event to position search map */}
                <TopbarMobile
                    handleMobileSearchOpen={handleMobileSearchOpen}
                    mobileRootClassName={mobileRootClassName}
                    mobileClassName={mobileClassName}
                    currentPage={currentPage}
                    intl={intl}
                />
                <BottomBarMobile currentPage={currentPage} />
                <div className={css.desktop}>
                    <TopbarDesktop
                        className={desktopClassName}
                        wishlistListingStatusChanged={wishlistListingStatusChanged}
                        currentUser={currentUser}
                        currentPage={currentPage}
                        initialSearchFormValues={initialSearchFormValues}
                        intl={intl}
                        isAuthenticated={isAuthenticated}
                        onLogout={handleLogOut}
                        searchAddress={searchAddress}
                        hasDraftListing={hasDraftListing}
                        profileNotComplete={profileNotComplete}
                        notificationCount={notificationCount}
                        {...rest}
                    />
                </div>
                <MobileSearchPanel
                    userType={userType}
                    isOpen={isMobileSearchOpen}
                    onClose={handleMobileSearchClose}
                />
                <GenericError />
                <CreditsNotificationModal />
                <DeleteAccountNotificationModal />
            </div>
        </TopbarContext.Provider>
    );
};

const Topbar = compose(withViewport, injectIntl)(TopbarComponent);

Topbar.displayName = 'Topbar';

export default Topbar;
